import { Component } from '@angular/core';
import { IProduct2, Product2, Company, FileVaultCategory } from 'situview-webapi-client';
import { AuthService, BreadcrumbService, StateService, CompanyViewService, ProductService } from 'src/app/services';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { Guid } from 'guid-typescript';

@Component({
  selector: 'new-product-edit',
  templateUrl: './new-product-edit.component.html',
  styleUrls: ['./new-product-edit.component.scss']
})

export class NewProductEditComponent extends SituBaseComponent {
  id: string = '';

  isAddMode: boolean = false;
  isAuthorizedForProductsCompany: boolean = false;

  product: INewProduct = new Product2();

  productCompany: Company | undefined;

  selectedTabIndex: number = 0;

  fileVaultCategory = FileVaultCategory;
  
  /* Query Parameters */
  readonly CallerReferenceKeyParamName: string = "callerReferenceKey";

  private _loading = 0;
  public get loading(): number { return this._loading; }

  public set loading(val: number) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val != 0);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private productService: ProductService,
    private companyViewService: CompanyViewService) {
    super(authService, stateService);

    this.id = this.route.snapshot.params['productId'];
    this.isAddMode = !this.id;
  }

  ngOnInit(): void {
    super.ngOnInit();

    // Read the query parameter and set the tab index
    this.route.queryParams.subscribe(params => {
      const tab = params['tab'];
      this.selectedTabIndex = tab ? +tab : 0; // Default to 0 if no tab param
    });
  }

  protected onSelectedCompanyChange(): void {
    if (this.isAddMode){
      this.createProduct();
    } else {
      this.loadProduct();
    }
  }

  private loadProduct(): void {
    this.loading++;

    this.productService.GetProduct(this.id, true, true, false, true)
    .then((product: IProduct2 | undefined) => {
      if (product) {
        this.product = product;
        this.getCompany(product.companyId);
        this.breadcrumbService.setParam('productSku', product.sku);
      } else {
        this.showMessage("There was an issue retrieving the product!");
      }
    })
    .catch((err: any) => {
      this.showMessage("Could not retrieve the product!");
    })
    .finally(() => {
      this.loading--;
    });
  }

  private createProduct(): void {
    this.product = new Product2();
    this.product.id = Guid.create().toString();
    this.product.companyId = this.selectedCompany?.id;
    this.product.name = "New Product";

    if (this.route.snapshot.queryParams[this.CallerReferenceKeyParamName]) {
      this.product.callerReferenceKey = this.route.snapshot.queryParams[this.CallerReferenceKeyParamName];
    }

    this.getCompany(this.product.companyId);
  }

  private getCompany(companyId: string | undefined) {
    
    this.isAuthorizedForProductsCompany = !!companyId && this.authService.isAuthorizedForCompanyById(companyId);

    this.loading++;

    return this.companyViewService.LoadCompanies()
      .then((companies: Company[]) => {
        this.productCompany = companies.find(e => e.id == companyId);
        this.loading--;
      });
  }

  onTabChanged(event: any): void {
    const selectedTab = event.index;
    
    // Update the URL with the selected tab index as a query parameter
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: selectedTab },
      queryParamsHandling: 'merge' // Preserve other query parameters
    });
  }

  readOnlyProductDetailsTab() {
    if (this.isSituUser) {
      return false; // Situ admin etc can always edit
    }

    if (this.isAuthorizedForProductsCompany &&
      (this.isAdmin)
    ) {
      return false; // Company admins can only edit their own products from a companies they are authorised to access
    }

    return true; // Everyone else has readonly access
  }

  canAccessProductThemesTab() {
    if (this.isAddMode) {
      return false; // This tab is never accessible in add mode
    }

    if (this.isSituUser) {
      return true; // Situ admin etc can access this page
    }

    return false;
  }

  canAccessProductWorkflowHistoryTab() {
    if (this.isAddMode) {
      return false; // This tab is never accessible in add mode
    }

    if (this.isSituUser) {
      return true; // Situ admin etc can access this page
    }

    return false;
  }

  canAccessReferenceMaterialFileVaultBrowserTab() {
    if (this.isAddMode) {
      return false; // This tab is never accessible in add mode
    }

    if (this.isSituUser) {
      return true; // Situ admin etc can access this page
    }

    if (this.isExternalProductContractorUser) {
      return true; // This user can always access the tab
    }

    if (this.isAuthorizedForProductsCompany &&
      (this.isCompanyProductPublisherUser)
    ) {
      return true; // These users can access the tab if the product is from a company they are authorised to access
    }

    return false;
  }

  canAccessSourceMaterialFileVaultBrowserTab() {
    if (this.isAddMode) {
      return false; // This tab is never accessible in add mode
    }

    if (this.isSituUser) {
      return true; // Situ admin etc can access this page
    }

    if (this.isExternalProductContractorUser) {
      return true; // This user can always access the tab
    }

    if (this.isAuthorizedForProductsCompany &&
      (this.isCompanyProductPublisherUser)
    ) {
      return true; // These users can access the tab if the product is from a company they are authorised to access
    }

    return false;
  }

  canAccessAssetsFileVaultBrowserTab() {
    if (this.isAddMode) {
      return false; // This tab is never accessible in add mode
    }

    if (this.isSituUser) {
      return true; // Situ admin etc can access this page
    }

    if (this.isExternalProductContractorUser) {
      return true; // This user can always access the tab
    }

    if (this.isAuthorizedForProductsCompany &&
      (this.isCompanyProductPublisherUser)
    ) {
      return true; // These users can access the tab if the product is from a company they are authorised to access
    }

    return false;
  }

  canAccessMarketingMaterialFileVaultBrowserTab() {
    if (this.isAddMode) {
      return false; // This tab is never accessible in add mode
    }

    if (this.isSituUser) {
      return true; // Situ admin etc can access this page
    }

    if (this.isAuthorizedForProductsCompany &&
      (this.isAdmin)
    ) {
      return true; // These users can access the tab if the product is from a company they are authorised to access
    }

    return false;
  }

  canAccessProductPublishTab() {
    if (this.isAddMode) {
      return false; // This tab is never accessible in add mode
    }

    if (this.isSituUser) {
      return true; // Situ admin etc can access this page
    }

    if (this.isExternalProductContractorUser) {
      return true; // This user can always access the tab
    }

    if (this.isAuthorizedForProductsCompany &&
      (this.isCompanyProductPublisherUser)
    ) {
      return true; // These users can access the tab if the product is from a company they are authorised to access
    }

    return false;
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

export interface INewProduct extends IProduct2 {

}
